import { useAuthHeader } from "react-auth-kit";

const apiUrl =
  process.env.REACT_APP_ENV === "LOCAL"
    ? "http://localhost:5000"
    : "https://api.spotprisprognos.se:8443";

export interface PredictionDetailData {
  id: number;
  targetTime: string;
  predictedValue: number;
  actualValue: number | null;
  distance: number | null;
  distancePercentage: number | null;
  directionCorrect: boolean | null;
  score: number | null;
  type: string;
  reasoning: string | null;
  generatedAt: string;
}

export interface PredictionDetailResponse {
  predictions: PredictionDetailData[];
  total: number;
  page: number;
  limit: number;
}

export interface UserActivityEntry {
  userId: string;
  email: string;
  username: string | null;
  planName: string | null;
  maxActiveDaysPerMonth: number | null;
  apiRequestsPerMonth: number | null;
  pageViews: number;
  areaToggles: number;
  ctaClicks: number;
  modalOpens: number;
  activeDays: number;
  activeDaysThisMonth: number;
  apiCallsThisMonth: number;
  lastActiveAt: string | null;
}

export interface UserEventEntry {
  eventType: string;
  target: string | null;
  metadata: Record<string, unknown> | null;
  createdAt: string;
}

// /api/analytics/models — sums (not means) so any set of zones can be pooled.
export interface ModelErrorSums {
  model: "standard" | "pro";
  area: string;
  n: number;
  sumAbs: number;
  sumSq: number;
  sumErr: number;
  naiveN: number;
  naiveSumAbs: number;
}
export interface ModelLeadRow extends ModelErrorSums {
  leadDay: number;
}
export interface ModelDayRow extends ModelErrorSums {
  day: string;
  shortN: number;
  shortSumAbs: number;
  shortNaiveN: number;
  shortNaiveSumAbs: number;
}
export interface ModelCheapRow {
  model: "standard" | "pro";
  area: string;
  leadDay: number;
  days: number;
  hits: number;
}
export interface ModelComparisonResponse {
  areas: string[];
  days: number;
  anchorHour: number;
  overlap: boolean;
  models: { key: string; name: string }[];
  byLead: ModelLeadRow[];
  byDay: ModelDayRow[];
  cheap: ModelCheapRow[];
}

export const useAnalyticsApi = () => {
  const authHeader = useAuthHeader();

  const fetchWithAuth = async (endpoint: string) => {
    const res = await fetch(`${apiUrl}${endpoint}`, {
      headers: { Authorization: authHeader() },
    });
    if (!res.ok) throw new Error(`API error: ${res.status}`);
    return res.json();
  };

  const fetchUserActivity = async (
    days = 30,
    limit = 50,
  ): Promise<{ windowDays: number; users: UserActivityEntry[] }> => {
    return fetchWithAuth(`/api/analytics/user-activity?days=${days}&limit=${limit}`);
  };

  const fetchUserEventStream = async (
    userId: string,
    limit = 100,
  ): Promise<{ events: UserEventEntry[] }> => {
    return fetchWithAuth(`/api/analytics/user-activity/${userId}?limit=${limit}`);
  };

  const fetchModelComparison = async (
    areas: string[],
    days: number,
    anchorHour: number,
    overlap: boolean,
  ): Promise<ModelComparisonResponse> => {
    const params = new URLSearchParams({
      areas: areas.join(","),
      days: String(days),
      anchorHour: String(anchorHour),
      overlap: overlap ? "1" : "0",
    });
    return fetchWithAuth(`/api/analytics/models?${params}`);
  };

  return {
    fetchModelComparison,
    fetchUserActivity,
    fetchUserEventStream,
  };
};
